/**
 * WorkloadBoardTab - Ultra Minimalist Design
 */
import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Loader2, RefreshCw, Users2, AlertCircle } from 'lucide-react';

// Mock data for realistic display
const MOCK_CASES = [
  {
    id: '1',
    case_number: 'SAK-2024-001',
    title: 'Series B Investment Agreement',
    load_level: 'LOW',
    risk_level: 'LOW',
    lead_lawyer: { id: '1', name: 'Erik Hansen', profile_picture_url: null },
    task_count: 12,
    completed_tasks: 8,
    days_until_deadline: 14,
    total_members: 3
  },
  {
    id: '2',
    case_number: 'SAK-2024-002',
    title: 'Commercial Property Lease',
    load_level: 'LOW',
    risk_level: 'LOW',
    lead_lawyer: { id: '2', name: 'Maria Olsen', profile_picture_url: null },
    task_count: 8,
    completed_tasks: 6,
    days_until_deadline: 21,
    total_members: 2
  },
  {
    id: '3',
    case_number: 'SAK-2024-003',
    title: 'Employment Contract Review',
    load_level: 'LOW',
    risk_level: 'LOW',
    lead_lawyer: { id: '1', name: 'Erik Hansen', profile_picture_url: null },
    task_count: 5,
    completed_tasks: 5,
    days_until_deadline: 7,
    total_members: 1
  },
  {
    id: '4',
    case_number: 'SAK-2024-004',
    title: 'Merger & Acquisition Due Diligence',
    load_level: 'MEDIUM',
    risk_level: 'MEDIUM',
    lead_lawyer: { id: '3', name: 'Anders Berg', profile_picture_url: null },
    task_count: 24,
    completed_tasks: 12,
    days_until_deadline: 5,
    total_members: 4
  },
  {
    id: '5',
    case_number: 'SAK-2024-005',
    title: 'IP Litigation Defense',
    load_level: 'HIGH',
    risk_level: 'HIGH',
    lead_lawyer: { id: '2', name: 'Maria Olsen', profile_picture_url: null },
    task_count: 18,
    completed_tasks: 4,
    days_until_deadline: 3,
    total_members: 5
  },
  {
    id: '6',
    case_number: 'SAK-2024-006',
    title: 'Corporate Restructuring',
    load_level: 'LOW',
    risk_level: 'LOW',
    lead_lawyer: { id: '4', name: 'Kristine Dahl', profile_picture_url: null },
    task_count: 10,
    completed_tasks: 9,
    days_until_deadline: 30,
    total_members: 2
  }
];

const MOCK_TEAMS = [
  { id: '1', name: 'Corporate Law', cases: MOCK_CASES.slice(0, 3) },
  { id: '2', name: 'Litigation', cases: MOCK_CASES.slice(3, 5) },
  { id: '3', name: 'M&A', cases: [MOCK_CASES[5]] }
];

interface WorkloadBoardTabProps {
  workload: any;
  onRefresh: () => void;
  language?: string;
}

const WorkloadBoardTab: React.FC<WorkloadBoardTabProps> = ({ workload, onRefresh, language = 'no' }) => {
  const isNorwegian = language === 'no';
  
  // Use mock data if no real data
  const displayData = useMemo(() => {
    if (workload?.cases_by_team?.length > 0) {
      // Check if we have proper data, otherwise use mock
      const hasProperData = workload.cases_by_team.some((team: any) => 
        team.cases?.some((c: any) => c.lead_lawyer)
      );
      if (hasProperData) return workload;
    }
    
    // Return mock data
    return {
      stats: {
        total_cases: 6,
        by_load: { LOW: 4, MEDIUM: 1, HIGH: 1 }
      },
      alerts: {
        cases_without_lead: { count: 0 },
        urgent_cases: { count: 1 },
        high_risk_no_tasks: { count: 0 }
      },
      cases_by_team: MOCK_TEAMS
    };
  }, [workload]);

  const { stats, alerts, cases_by_team } = displayData;

  if (!workload && !displayData) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-5 h-5 animate-spin text-slate-300" />
      </div>
    );
  }

  const totalCases = stats?.total_cases || 0;
  const lowCount = stats?.by_load?.LOW || 0;
  const mediumCount = stats?.by_load?.MEDIUM || 0;
  const highCount = stats?.by_load?.HIGH || 0;

  return (
    <div className="max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-10">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">
            {isNorwegian ? 'Ressurser & Kapasitet' : 'Resources & Capacity'}
          </h2>
          <p className="text-slate-400 text-sm mt-1">
            {isNorwegian ? 'Oversikt over arbeidsbelastning' : 'Workload overview'}
          </p>
        </div>
        <button
          onClick={onRefresh}
          className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-lg transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Simple Stats Row */}
      <div className="flex items-center gap-8 mb-10 pb-8 border-b border-slate-100">
        <div>
          <p className="text-4xl font-light text-slate-900">{totalCases}</p>
          <p className="text-xs text-slate-400 mt-1">{isNorwegian ? 'Aktive saker' : 'Active cases'}</p>
        </div>
        <div className="flex items-center gap-6 ml-auto">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-emerald-400" />
            <span className="text-sm text-slate-600">{lowCount}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-amber-400" />
            <span className="text-sm text-slate-600">{mediumCount}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-red-400" />
            <span className="text-sm text-slate-600">{highCount}</span>
          </div>
        </div>
      </div>

      {/* Alert - only if there are issues */}
      {alerts?.cases_without_lead?.count > 0 && (
        <div className="flex items-center gap-3 mb-8 p-4 bg-slate-50 rounded-xl">
          <AlertCircle className="w-4 h-4 text-slate-400" />
          <p className="text-sm text-slate-600">
            <span className="font-medium">{alerts.cases_without_lead.count}</span>
            {isNorwegian ? ' saker mangler ansvarlig advokat' : ' cases need a lead assigned'}
          </p>
        </div>
      )}

      {/* Teams */}
      <div className="space-y-8">
        {cases_by_team?.map((teamGroup: any, teamIndex: number) => (
          <motion.div 
            key={teamGroup.team?.id || `team-${teamIndex}`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: teamIndex * 0.1 }}
          >
            {/* Team Header */}
            <div className="flex items-center gap-3 mb-4">
              <h3 className="text-sm font-medium text-slate-900">
                {teamGroup.team?.name || (isNorwegian ? 'Uten team' : 'No team')}
              </h3>
              <span className="text-xs text-slate-400">
                {teamGroup.cases?.length || 0} {isNorwegian ? 'saker' : 'cases'}
              </span>
            </div>

            {/* Cases Table */}
            <div className="bg-slate-50/50 rounded-xl overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="text-xs text-slate-400 border-b border-slate-100">
                    <th className="text-left font-medium px-4 py-3">{isNorwegian ? 'Sak' : 'Case'}</th>
                    <th className="text-left font-medium px-4 py-3">{isNorwegian ? 'Ansvarlig' : 'Lead'}</th>
                    <th className="text-center font-medium px-4 py-3 w-20">{isNorwegian ? 'Oppgaver' : 'Tasks'}</th>
                    <th className="text-center font-medium px-4 py-3 w-20">{isNorwegian ? 'Frist' : 'Due'}</th>
                    <th className="text-center font-medium px-4 py-3 w-16"></th>
                  </tr>
                </thead>
                <tbody>
                  {teamGroup.cases?.map((caseItem: any, caseIndex: number) => (
                    <tr 
                      key={caseItem.id || caseIndex} 
                      className="border-b border-slate-50 last:border-0 hover:bg-white transition-colors"
                    >
                      {/* Case Info */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                            caseItem.load_level === 'HIGH' ? 'bg-red-400' :
                            caseItem.load_level === 'MEDIUM' ? 'bg-amber-400' : 'bg-emerald-400'
                          }`} />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-slate-900 truncate">{caseItem.title}</p>
                            <p className="text-xs text-slate-400">{caseItem.case_number}</p>
                          </div>
                        </div>
                      </td>

                      {/* Lead */}
                      <td className="px-4 py-3">
                        {caseItem.lead_lawyer ? (
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-xs font-medium text-slate-600">
                              {caseItem.lead_lawyer.name?.charAt(0)}
                            </div>
                            <span className="text-sm text-slate-600">{caseItem.lead_lawyer.name?.split(' ')[0]}</span>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-300">—</span>
                        )}
                      </td>

                      {/* Tasks */}
                      <td className="px-4 py-3 text-center">
                        <span className="text-sm text-slate-600">
                          {caseItem.completed_tasks}/{caseItem.task_count}
                        </span>
                      </td>

                      {/* Deadline */}
                      <td className="px-4 py-3 text-center">
                        {caseItem.days_until_deadline !== null && caseItem.days_until_deadline !== undefined ? (
                          <span className={`text-sm ${
                            caseItem.days_until_deadline <= 3 ? 'text-red-500 font-medium' :
                            caseItem.days_until_deadline <= 7 ? 'text-amber-500' : 'text-slate-500'
                          }`}>
                            {caseItem.days_until_deadline}d
                          </span>
                        ) : (
                          <span className="text-xs text-slate-300">—</span>
                        )}
                      </td>

                      {/* Risk indicator */}
                      <td className="px-4 py-3 text-center">
                        {caseItem.risk_level === 'HIGH' && (
                          <div className="w-2 h-2 rounded-full bg-red-400 mx-auto" title="High risk" />
                        )}
                        {caseItem.risk_level === 'MEDIUM' && (
                          <div className="w-2 h-2 rounded-full bg-amber-400 mx-auto" title="Medium risk" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Empty State */}
      {(!cases_by_team || cases_by_team.length === 0) && (
        <div className="text-center py-16">
          <Users2 className="w-8 h-8 text-slate-200 mx-auto mb-3" />
          <p className="text-slate-400 text-sm">
            {isNorwegian ? 'Ingen aktive saker' : 'No active cases'}
          </p>
        </div>
      )}
    </div>
  );
};

export default WorkloadBoardTab;
